import { Box, Skeleton, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import * as React from "react";

import { useAuth } from "@/providers";
import { CommentEntityType, CommentRecord } from "@/shared/domain/comments";
import {
  useCommentsForEntity,
  useDeleteComment,
} from "@/shared/domain/comments/hooks";

import { Comment } from "./Comment";
import { CommentForm } from "./CommentForm";
import { CommentList } from "./CommentList";
import { CommentPagination } from "./CommentPagination";
import { DeleteCommentConfirmation } from "./DeleteCommentConfirmation";

interface EntityCommentsProps {
  entityType: CommentEntityType;
  entityId: string;
  pageSize?: 10 | 25 | 50;
  enableTimestamps?: boolean;
}

export const EntityComments: React.FC<EntityCommentsProps> = ({
  entityType,
  entityId,
  pageSize = 10,
  enableTimestamps = false,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<10 | 25 | 50>(pageSize);

  const { orgRoles, currentUser } = useAuth();
  const theme = useTheme();

  const [avatarMap, setAvatarMap] = useState<{ [key: string]: string }>({});
  const [commentToDelete, setCommentToDelete] = useState<CommentRecord | null>(
    null,
  );

  // Fetch comments for this entity
  const {
    data: comments = [],
    isPending,
    isFetching,
    hasNextPage,
    fetchNextPage,
  } = useCommentsForEntity({
    entityType,
    entityId,
  });

  const deleteCommentMutation = useDeleteComment();

  // Build avatar map from org roles
  useEffect(() => {
    if (orgRoles === null) {
      return;
    }

    const newAvatarMap: { [key: string]: string } = {};
    orgRoles.forEach((role) => {
      newAvatarMap[role.user.user_id] = role.user.picture_url ?? "";
    });

    setAvatarMap(newAvatarMap);
  }, [orgRoles]);

  // Fetch more pages if needed for pagination
  useEffect(() => {
    const neededComments = (page + 1) * rowsPerPage;
    if (comments.length < neededComments && hasNextPage && !isFetching) {
      void fetchNextPage();
    }
  }, [
    page,
    rowsPerPage,
    comments.length,
    hasNextPage,
    isFetching,
    fetchNextPage,
  ]);

  const isFirstLoading = isPending && !isFetching;

  // Paginate comments client-side
  const displayedComments = comments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10) as 10 | 25 | 50);
    setPage(0);
  };

  const handleDeleteComment = (commentId: string) => {
    deleteCommentMutation.mutate(commentId);
  };

  return (
    <div>
      {!isFirstLoading ? (
        <>
          <div>
            <CommentForm
              entityType={entityType}
              entityId={entityId}
              enableTimestamps={enableTimestamps}
            />
          </div>

          {/* List of comments */}
          <Box sx={{ mb: theme.spacing(2) }}>
            <CommentList>
              {displayedComments.map((comment) => {
                const avatarUrl = avatarMap[comment.created_by] ?? "";
                return (
                  <Comment
                    key={comment.comment_id}
                    comment={comment}
                    avatarUrl={avatarUrl}
                    currentUserId={currentUser?.user_id ?? ""}
                    onDelete={() => setCommentToDelete(comment)}
                  />
                );
              })}

              {/* Pagination */}
              {comments.length > 0 && (
                <CommentPagination
                  component="div"
                  count={comments.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              )}
            </CommentList>
          </Box>

          {/* Delete confirmation modal */}
          <DeleteCommentConfirmation
            comment={commentToDelete}
            isOpen={commentToDelete !== null}
            onClose={() => setCommentToDelete(null)}
            onDelete={handleDeleteComment}
          />
        </>
      ) : (
        <Skeleton />
      )}
    </div>
  );
};
